import React from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import type { ProcessingJob, RetentionLifecycle } from "./processingTypes";
import { CHECKPOINT_LABELS } from "./processingTypes";
import {
  type JobOverallStatus,
  formatDurationSince,
  formatDateTime,
} from "./processingDisplay";
import { deriveParkReasonCode } from "./lifecycleModals";

// Operator Guidance panel (owner 2026-07-19). At every point in the workflow
// the operator must immediately know: WHAT is happening, WHY, and WHAT to do
// next. Each of the five situations gets its own experience — there is
// deliberately no generic "Mark as Failed" anywhere.

/** Expected wall-clock time for each pipeline checkpoint. Every stage
 * completes in well under a minute today; listed per-key so future slower
 * stages can differ. Surfaced in the Stalled experience so the operator can
 * judge "17 days at a <1 minute step" at a glance. */
const EXPECTED_CHECKPOINT_TIME: Record<string, string> = {
  "audio-processing": "< 2 minutes",
};
const DEFAULT_EXPECTED_TIME = "< 1 minute";

function expectedTimeFor(checkpointKey: string | undefined | null): string {
  if (!checkpointKey) return DEFAULT_EXPECTED_TIME;
  return EXPECTED_CHECKPOINT_TIME[checkpointKey] ?? DEFAULT_EXPECTED_TIME;
}

/** Plain-language explanations for review-park reason codes. Raw enums never
 * lead; the code is appended in parentheses for support conversations. */
const REVIEW_REASON_TEXT: Record<string, string> = {
  "metadata-insufficient":
    "Not enough metadata could be read from the file to identify this song automatically.",
  "metadata-low-confidence":
    "The metadata found for this song was too uncertain to continue automatically.",
  "metadata-provider-conflict":
    "The metadata providers disagreed about what this song is.",
  "identification-disabled":
    "Automatic song identification is currently disabled, so every song needs a manual review.",
  "identification-low-confidence":
    "The identification providers could not identify this song with enough confidence.",
  "provider-conflict":
    "The identification providers returned conflicting matches for this song.",
  "provider-unavailable":
    "The identification providers were unavailable when this song was processed.",
  "label-unresolved":
    "The song was identified, but its record label could not be resolved.",
  "confidence-review-band":
    "The song was identified, but the match confidence falls in the range that requires an operator to confirm it.",
};

/** PJH — plain-language labels for system terminalization reason codes
 * (pipeline failure containment + stall sweeper). Raw code still shown in
 * parentheses for support conversations. */
const SYSTEM_TERMINALIZE_REASON_TEXT: Record<string, string> = {
  "pipeline-execution-failed":
    "The processing pipeline failed partway through and the system closed this job out automatically",
  "stalled-terminalized-by-system":
    "This job stopped making progress and the system closed it out automatically",
};

/**
 * Trackless terminal-job lifecycle note — Class C/D/E from backend
 * retentionLifecycle (never hard-code 90 days in the UI).
 * Layout is labeled + short lines so operators can scan at a glance.
 */
function TracklessLifecycleNote({
  retention,
}: {
  retention: RetentionLifecycle | null | undefined;
}): React.ReactElement {
  const classification = retention?.classification;
  const sectionLabel: React.CSSProperties = {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
    marginTop: 10,
    fontWeight: 700,
    color: "inherit",
  };
  const bulletList: React.CSSProperties = {
    margin: "0 0 0 18px",
    padding: 0,
    lineHeight: 1.5,
  };

  if (classification === "C" && retention) {
    const days = retention.retentionDays;
    const eligibleAt = retention.automaticCleanupEligibleAt
      ? formatDateTime(retention.automaticCleanupEligibleAt)
      : null;
    return (
      <div data-testid="guidance-trackless-lifecycle">
        <p style={{ marginBottom: 0 }}>
          This exact file remains blocked during the
          {days != null ? ` ${days}-day` : ""} retention period.
        </p>

        <div style={sectionLabel}>What KrossKast will do automatically</div>
        <ul style={bulletList} data-testid="guidance-auto-actions">
          <li>
            {eligibleAt ? (
              <>
                Beginning {eligibleAt}, archive this failed Processing Job
              </>
            ) : (
              <>After the retention period, archive this failed Processing Job</>
            )}
          </li>
          <li>
            Permanently delete it through the governed cleanup process
          </li>
          <li>
            Release the checksum so the exact file can be uploaded again
          </li>
        </ul>

        <div style={sectionLabel}>What remains afterward</div>
        <ul style={bulletList} data-testid="guidance-what-remains">
          <li>Permanent audit / tombstone history</li>
        </ul>

        {eligibleAt ? (
          <div
            style={{ marginTop: 10 }}
            data-testid="guidance-cleanup-eligible-at"
          >
            <div style={sectionLabel}>Automatic cleanup eligible</div>
            <div>{eligibleAt}</div>
            <div style={{ fontSize: 12.5, opacity: 0.85, marginTop: 2 }}>
              {retention.eligible
                ? "Now eligible — archive and permanent delete may run on the next daily cleanup."
                : "Eligibility is when cleanup may begin; the daily scheduler may run later (not an exact deletion timestamp)."}
            </div>
          </div>
        ) : null}

        <div style={sectionLabel}>Want to act sooner?</div>
        <ul style={bulletList} data-testid="guidance-act-sooner">
          <li>Reprocess — retries from the retained source file</li>
          <li>
            Permanent Delete — releases the checksum now, without waiting for
            automatic archive and permanent delete
          </li>
        </ul>

        <p style={{ marginTop: 10, marginBottom: 0 }} data-testid="guidance-operator-action">
          <strong>Operator action:</strong> OPTIONAL — KrossKast owns the next
          lifecycle step. You can leave this alone.
        </p>
      </div>
    );
  }

  if (classification === "D") {
    return (
      <div data-testid="guidance-trackless-lifecycle">
        <p style={{ marginBottom: 0 }}>
          This exact file remains blocked for security reasons. KrossKast does
          not automatically archive or permanently delete malware or security
          rejects.
        </p>
        <p style={{ marginTop: 10, marginBottom: 0 }} data-testid="guidance-operator-action">
          <strong>Operator action:</strong> REQUIRED — permanently delete this
          job to release duplicate detection. There is no automatic cleanup
          date.
        </p>
      </div>
    );
  }

  if (classification === "E") {
    return (
      <div data-testid="guidance-trackless-lifecycle">
        <p style={{ marginBottom: 0 }}>
          This exact file remains blocked until an operator decides. KrossKast
          does not automatically archive or permanently delete operator or
          business rejections.
        </p>
        <p style={{ marginTop: 10, marginBottom: 0 }} data-testid="guidance-operator-action">
          <strong>Operator action:</strong> REQUIRED for Permanent Delete (or
          other lifecycle choice). There is no automatic cleanup date.
        </p>
      </div>
    );
  }

  // Unclassified / missing API field — fail-safe: do not promise auto archive/PD.
  return (
    <div data-testid="guidance-trackless-lifecycle">
      <p style={{ marginBottom: 0 }}>
        This exact file still blocks duplicate upload. Re-uploading the same
        file is stopped until this job is permanently deleted.
      </p>
      <p style={{ marginTop: 10, marginBottom: 0 }} data-testid="guidance-operator-action">
        <strong>Operator action:</strong> REQUIRED if you need to permanently
        delete this job and release duplicate detection — automatic archive and
        permanent delete do not apply to this outcome.
      </p>
    </div>
  );
}

/** Plain-language lines for each unresolved catalog dependency (parsed from
 * the catalog-linking checkpoint detail: "unresolved: artist-not-found, …"). */
function catalogDependencyLines(job: ProcessingJob): string[] {
  const cp = (job.checkpoints ?? []).find((c) => c.key === "catalog-linking");
  const tokens = (cp?.detail || "")
    .replace(/^unresolved:\s*/i, "")
    .split(/[,\s]+/)
    .filter(Boolean);
  const artist = job.discoveredArtist || job.artistNameHint || job.intake?.artistNameHint || "";
  const album = job.discoveredAlbum || "";
  const lines: string[] = [];
  for (const token of tokens) {
    switch (token) {
      case "artist-not-found":
        lines.push(
          artist
            ? `Waiting for Artist record — no Artist named “${artist}” exists in the catalog yet.`
            : "Waiting for Artist record — the required Artist does not exist in the catalog yet.",
        );
        break;
      case "artist-ambiguous":
        lines.push(
          artist
            ? `Multiple Artist records match “${artist}” — the pipeline cannot choose one safely.`
            : "Multiple Artist records match — the pipeline cannot choose one safely.",
        );
        break;
      case "album-not-found":
        lines.push(
          album
            ? `Waiting for Album creation — no Album named “${album}” exists for this artist yet.`
            : "Waiting for Album creation — the required Album does not exist yet.",
        );
        break;
      case "album-ambiguous":
        lines.push("Multiple Album records match — the pipeline cannot choose one safely.");
        break;
      // Record-label vocabulary (lib/catalog-link.js): commercial labels come
      // from identified metadata; house labels back KK Originals.
      case "commercial-label-not-found":
        lines.push("Waiting for Record Label — the identified record label does not exist in the catalog yet.");
        break;
      case "commercial-label-ambiguous":
        lines.push("Multiple Record Label records match the identified label — the pipeline cannot choose one safely.");
        break;
      case "commercial-label-unresolvable":
        lines.push("Waiting for Record Label — no record label could be identified for this song.");
        break;
      case "house-label-not-found":
        lines.push("Waiting for the KrossKast house Record Label — it does not exist as an active catalog record yet.");
        break;
      case "house-label-ambiguous":
        lines.push("Multiple catalog records match the KrossKast house Record Label — the pipeline cannot choose one safely.");
        break;
      case "title-missing":
        lines.push("The song has no usable Title yet — confirm it in review.");
        break;
      case "artist-name-missing":
        lines.push("The song has no usable Artist name yet — confirm it in review.");
        break;
      default:
        lines.push(`Waiting on catalog dependency: ${token}.`);
    }
  }
  return lines;
}

const PANEL_STYLE: Record<string, React.CSSProperties> = {
  blue: { background: "#f0f6ff", border: "1px solid #b6d4fe" },
  green: { background: "#f2faf5", border: "1px solid #badbcc" },
  amber: { background: "#fffaf0", border: "1px solid #ffe69c" },
  orange: { background: "#fff6ef", border: "1px solid #fecba1" },
  red: { background: "#fdf3f4", border: "1px solid #f5c2c7" },
  grey: { background: "#f8f9fa", border: "1px solid #dee2e6" },
};

function Panel({
  tone,
  icon,
  iconColor,
  heading,
  children,
  actions,
}: {
  tone: keyof typeof PANEL_STYLE;
  icon: string;
  iconColor: string;
  heading: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-testid="guidance-panel"
      data-guidance-tone={tone}
      style={{ ...PANEL_STYLE[tone], borderRadius: 8, padding: "14px 18px", marginBottom: 16 }}
    >
      <div style={{ display: "flex", gap: 12 }}>
        <i
          className={`bi ${icon}`}
          style={{ color: iconColor, fontSize: 22, lineHeight: 1.2 }}
          aria-hidden="true"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15.5, marginBottom: 4 }}>{heading}</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{children}</div>
          {actions ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function fact(label: string, value: React.ReactNode): React.ReactElement {
  return (
    <div style={{ minWidth: 160 }}>
      <div style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.65 }}>
        {label}
      </div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export interface JobGuidancePanelProps {
  job: ProcessingJob;
  status: JobOverallStatus;
  canEdit: boolean;
  canReprocess: boolean;
  busy?: boolean;
  onResumeReview: () => void;
  /** Inspect confidence / identification audit without changing automation. */
  onViewConfidence?: () => void;
  /** Omit for governed jobs — Song Library owns Archive. */
  onArchive?: () => void;
  onReprocess: () => void;
}

/**
 * Underlying processing terminal outcome for Operator Guidance.
 *
 * Archive is presentation / list-lifecycle state (see ARCHIVED banner). It is
 * NOT a replacement for failed/not_approved. Callers must use this (via
 * resolveGuidanceStatus) so `status === "archived"` cannot erase the terminal
 * decision that Class C/D/E TracklessLifecycleNote depends on.
 *
 * Decision takes precedence over state (same order as deriveJobStatus).
 */
export function underlyingTerminalGuidanceStatus(
  job: ProcessingJob,
): "failed" | "not_approved" | null {
  if (job.decision === "not_approved") return "not_approved";
  if (job.decision === "failed") return "failed";
  if (job.state === "not_approved") return "not_approved";
  if (job.state === "failed") return "failed";
  return null;
}

/**
 * Resolve which guidance branch to render.
 *
 * INVARIANT — archived Class C (and other trackless terminal) guidance:
 *   - Active + trackless + failed/not_approved + Class C → Class C note MUST render
 *   - Archived + trackless + failed/not_approved + Class C → ARCHIVED banner AND
 *     Class C note MUST both render (archive alone must NEVER suppress it)
 *   - Unrelated archived jobs stay banner-only (return null here)
 *
 * Forbidden refactor: `if (status === "archived") return null` as a blanket
 * early exit in JobGuidancePanel — that hid 90-day Class C copy after manual
 * archive. Keep archive orthogonal to the underlying processing outcome.
 */
export function resolveGuidanceStatus(
  job: ProcessingJob,
  status: JobOverallStatus,
): JobOverallStatus | null {
  if (status !== "archived") return status;

  // Archive alone must not erase terminal lifecycle guidance.
  const trackless = !job.trackId && !job.lineageTrackId;
  if (!trackless) return null;

  return underlyingTerminalGuidanceStatus(job);
}

const JobGuidancePanel: React.FC<JobGuidancePanelProps> = ({
  job,
  status,
  canEdit,
  canReprocess,
  busy = false,
  onResumeReview,
  onViewConfidence,
  onArchive,
  onReprocess,
}) => {
  const navigate = useNavigate();
  const checkpointLabel = job.currentCheckpoint
    ? CHECKPOINT_LABELS[job.currentCheckpoint] ?? job.currentCheckpoint
    : "—";
  const viewOnlyTitle = "You have view-only access to the uploader";

  const guidanceStatus = resolveGuidanceStatus(job, status);
  if (guidanceStatus == null) return null;

  if (guidanceStatus === "processing") {
    return (
      <Panel
        tone="blue"
        icon="bi-arrow-repeat"
        iconColor="#084298"
        heading={`Processing — ${checkpointLabel} is running now.`}
      >
        The pipeline advances through each checkpoint automatically; each step
        normally takes {expectedTimeFor(job.currentCheckpoint)}. No action is
        needed — this page refreshes every 5 seconds, so you can watch progress
        live.
      </Panel>
    );
  }

  if (guidanceStatus === "stalled") {
    return (
      <Panel
        tone="orange"
        icon="bi-exclamation-triangle-fill"
        iconColor="#8a3c00"
        heading="This Song Processing job appears to have stopped progressing."
        actions={
          onArchive ? (
            <Button
              variant="outline-dark"
              size="sm"
              disabled={!canEdit || busy}
              title={
                canEdit
                  ? "Finalize this stalled job as Not Approved and move it to the Archived tab (audited)"
                  : viewOnlyTitle
              }
              onClick={onArchive}
              data-testid="stalled-archive"
            >
              <i className="bi bi-archive" aria-hidden="true" /> Archive this job
            </Button>
          ) : (
            <span className="text-muted" style={{ fontSize: 13 }}>
              This job belongs to a living Song — manage lifecycle from Song
              Library (Reprocess stays available here).
            </span>
          )
        }
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 32px", margin: "6px 0 10px" }}>
          {fact("Last checkpoint", checkpointLabel)}
          {fact("No activity for", formatDurationSince(job.updatedAt))}
          {fact("Expected processing time", expectedTimeFor(job.currentCheckpoint))}
        </div>
        <p style={{ marginBottom: 6 }}>
          This job was likely interrupted by a deployment or infrastructure
          event and will not resume on its own. The uploaded file and the
          job&apos;s full history are safely retained — nothing is lost.
        </p>
        <p className="mb-0">
          <strong>What to do next:</strong> archive this job (it is finalized
          as Not Approved with a &ldquo;stalled&rdquo; audit reason), then
          upload the song again to run it through the current pipeline.
        </p>
      </Panel>
    );
  }

  if (guidanceStatus === "waiting_review") {
    const reasonCode = deriveParkReasonCode(job);
    const reasonText =
      REVIEW_REASON_TEXT[reasonCode] ||
      "This song needs an operator to confirm its metadata before processing can continue.";
    return (
      <Panel
        tone="amber"
        icon="bi-person-check-fill"
        iconColor="#997404"
        heading="Waiting for Review — operator action required."
        actions={
          <>
            {onViewConfidence ? (
              <Button
                variant="outline-dark"
                size="sm"
                onClick={onViewConfidence}
                data-testid="guidance-view-confidence"
              >
                Decision Audit
              </Button>
            ) : null}
            <Button
              variant="warning"
              size="sm"
              disabled={!canEdit || busy}
              title={canEdit ? "Review the identified metadata and continue processing" : viewOnlyTitle}
              onClick={onResumeReview}
              data-testid="guidance-resume"
            >
              Resume Review
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: 6 }}>
          KrossKast is intentionally waiting for an operator to review this song
          — this is not an automated cleanup path.
        </p>
        <p style={{ marginBottom: 6 }}>
          {reasonText}
          {reasonCode ? <span className="text-muted"> ({reasonCode})</span> : null}
        </p>
        <p style={{ marginBottom: 6 }} data-testid="guidance-operator-action">
          <strong>Operator action:</strong> REQUIRED — open Resume Review to
          continue, or Archive if this attempt should be closed without
          publishing.
        </p>
        <p className="mb-0">
          <strong>What to do next:</strong> open Resume Review, confirm or
          correct the song details, and processing continues automatically from
          where it parked.
        </p>
      </Panel>
    );
  }

  if (guidanceStatus === "waiting_catalog") {
    const dependencies = catalogDependencyLines(job);
    const isOriginal = job.songType === "kk-original";
    return (
      <Panel
        tone="orange"
        icon="bi-collection-fill"
        iconColor="#8a3c00"
        heading="Waiting for Catalog Records — processing is paused on a dependency."
        actions={
          <>
            <Button
              variant="warning"
              size="sm"
              disabled={!canEdit || busy}
              title={
                canEdit
                  ? "Re-run catalog linking after the missing records exist"
                  : viewOnlyTitle
              }
              onClick={onResumeReview}
              data-testid="guidance-resume-catalog"
            >
              Resume Catalog Linking
            </Button>
            <Button variant="outline-dark" size="sm" onClick={() => navigate("/artists")}>
              Open Artists
            </Button>
            <Button variant="outline-dark" size="sm" onClick={() => navigate("/albums")}>
              Open Albums
            </Button>
            <Button variant="outline-dark" size="sm" onClick={() => navigate("/record-labels")}>
              Open Record Labels
            </Button>
          </>
        }
      >
        <p style={{ marginBottom: 6 }}>
          {isOriginal
            ? "KrossKast normally creates or locates the required catalog records for KK Originals automatically. This job paused because a required record could not be resolved:"
            : "This song's catalog records could not be resolved automatically:"}
        </p>
        <ul style={{ marginBottom: 6, paddingLeft: 20 }}>
          {(dependencies.length > 0
            ? dependencies
            : ["Waiting on catalog records — the specific dependency was not recorded."]
          ).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className="mb-0">
          <strong>What to do next:</strong> create or fix the records above,
          then Resume Catalog Linking — the pipeline re-checks the catalog and
          continues automatically.
        </p>
      </Panel>
    );
  }

  if (guidanceStatus === "failed") {
    const failedLabel = job.failedCheckpoint
      ? CHECKPOINT_LABELS[job.failedCheckpoint] ?? job.failedCheckpoint
      : null;
    const reasonCode = deriveParkReasonCode(job);
    const trackless = !job.trackId && !job.lineageTrackId;
    const systemReasonText = reasonCode
      ? SYSTEM_TERMINALIZE_REASON_TEXT[reasonCode]
      : undefined;
    return (
      <Panel
        tone="red"
        icon="bi-x-circle-fill"
        iconColor="#842029"
        heading={
          trackless
            ? "Processing failed — no Track was created from this processing attempt."
            : failedLabel
              ? `Processing stopped — ${failedLabel} failed.`
              : "Processing stopped — this job failed."
        }
        actions={
          canReprocess ? (
            <Button
              variant="outline-primary"
              size="sm"
              disabled={!canEdit || busy}
              title={canEdit ? "Run this song through the pipeline again from the retained source file" : viewOnlyTitle}
              onClick={onReprocess}
              data-testid="guidance-reprocess"
            >
              Reprocess
            </Button>
          ) : undefined
        }
      >
        <p style={{ marginBottom: 6 }}>
          {trackless && failedLabel ? `${failedLabel} failed. ` : null}
          {systemReasonText ?? "The pipeline hit a condition it could not recover from"}
          {reasonCode ? (
            <>
              {" "}
              (<span className="text-muted">{reasonCode}</span>)
            </>
          ) : null}
          . The uploaded file and full history are retained.
        </p>
        {trackless ? (
          <TracklessLifecycleNote retention={job.retentionLifecycle} />
        ) : null}
        {!(trackless && job.retentionLifecycle?.classification === "C") ? (
          <p className="mb-0" style={{ marginTop: 10 }}>
            <strong>What to do next:</strong> Reprocess runs the song through the
            pipeline again from the retained source file — no re-upload needed. If
            it fails the same way, the file itself likely needs attention.
          </p>
        ) : null}
      </Panel>
    );
  }

  if (guidanceStatus === "not_approved") {
    const reasonCode = deriveParkReasonCode(job);
    const trackless = !job.trackId && !job.lineageTrackId;
    return (
      <Panel
        tone="grey"
        icon="bi-slash-circle-fill"
        iconColor="#41464b"
        heading={
          trackless
            ? "Not Approved — no Track was created from this processing attempt."
            : "Not Approved — this job reached a final outcome without publishing."
        }
      >
        <p style={{ marginBottom: 6 }}>
          {reasonCode === "stalled-archived-by-operator"
            ? "An operator archived this job after it stalled — the pipeline run was superseded, not the song itself."
            : reasonCode === "archived-by-operator"
              ? "An operator archived this job while it was waiting for review."
              : reasonCode === "superseded-by-pipeline-upgrade"
                ? "This job ran on an earlier pipeline version and was closed out during a platform upgrade."
                : "This song did not pass processing."}
          {reasonCode ? <span className="text-muted"> ({reasonCode})</span> : null}
        </p>
        {trackless ? (
          <TracklessLifecycleNote retention={job.retentionLifecycle} />
        ) : null}
        {!(trackless && job.retentionLifecycle?.classification === "C") ? (
          <p className="mb-0" style={{ marginTop: 10 }}>
            <strong>What to do next:</strong>{" "}
            {trackless
              ? "Permanent Delete is required to release duplicate detection for this exact file, then upload again as a fresh Song Processing job if needed."
              : "Nothing is required. To publish this song, upload it again — a fresh Song Processing job will run through the current pipeline."}
          </p>
        ) : null}
      </Panel>
    );
  }

  if (guidanceStatus === "approved") {
    return (
      <Panel
        tone="green"
        icon="bi-check-circle-fill"
        iconColor="#0f5132"
        heading="Approved — this song processed successfully and is ready for playback."
        actions={
          onViewConfidence ? (
            <Button
              variant="outline-dark"
              size="sm"
              onClick={onViewConfidence}
              data-testid="guidance-view-confidence"
            >
              Decision Audit
            </Button>
          ) : undefined
        }
      >
        <p style={{ marginBottom: onViewConfidence ? 6 : 0 }}>
          No action needed. The approved audio is published under this
          song&apos;s Track{job.trackId ? "" : " (linked at finalize)"}, and
          Reprocess remains available if the audio ever needs to be regenerated
          from the retained source file.
        </p>
        {onViewConfidence ? (
          <p className="mb-0">
            <strong>Audit:</strong> open the Decision Audit to inspect the
            automated decision (score, thresholds, providers, and final
            metadata) — automation stays; auditability is always available.
          </p>
        ) : null}
      </Panel>
    );
  }

  if (guidanceStatus === "upload_expired") {
    return (
      <Panel
        tone="grey"
        icon="bi-hourglass-bottom"
        iconColor="#41464b"
        heading="Upload Expired — the file never finished uploading."
      >
        The upload reservation expired before the file arrived, so processing
        never started. <strong>What to do next:</strong> upload the song again
        from the Uploader.
      </Panel>
    );
  }

  return null;
};

export default JobGuidancePanel;
